export interface Destination {
  id: number;
  name: string;
  location: string;
  region: string;
  description: string;
  image: string;
  /** Descriptive, SEO-friendly alt text for the image. */
  alt: string;
  price: string;
  rating: number;
  features: string[];
  category: 'beach' | 'heritage' | 'mountain' | 'nature';
}

// All photos are real, freely-licensed images of each actual location,
// sourced from Wikimedia Commons via Special:FilePath (CC BY-SA).
export const destinations: Destination[] = [
  {
    id: 1,
    name: 'Ksamil Beach',
    location: 'Ksamil, Albanian Riviera',
    region: 'Albanian Riviera',
    description: 'Albania\'s answer to the Maldives — pristine white sand beaches with crystal-clear turquoise waters and enchanting offshore islands just a swim away.',
    image: 'https://commons.wikimedia.org/wiki/Special:FilePath/Ksamil,_Albania_-_beach.JPG?width=1200',
    alt: 'Ksamil Beach on the Albanian Riviera with turquoise Ionian Sea water, white sand and small offshore islands — luxury Albania travel destination',
    price: 'From €120',
    rating: 4.9,
    features: ['Private Beach', 'Island Hopping', 'Snorkeling'],
    category: 'beach',
  },
  {
    id: 2,
    name: 'Sarandë Riviera',
    location: 'Sarandë, Southern Albania',
    region: 'Albanian Riviera',
    description: 'A sun-drenched coastal gem facing Corfu, where ancient ruins meet vibrant promenades and the Ionian Sea sparkles in every shade of blue.',
    image: 'https://commons.wikimedia.org/wiki/Special:FilePath/Skyline_of_Sarande,_Albania_(facing_Greek_Islands).jpg?width=1200',
    alt: 'Saranda, southern Albania skyline facing the Greek island of Corfu across the blue Ionian Sea — Albanian Riviera travel guide',
    price: 'From €95',
    rating: 4.8,
    features: ['Waterfront Dining', 'Nightlife', 'Corfu Ferry'],
    category: 'beach',
  },
  {
    id: 3,
    name: 'Himarë Coast',
    location: 'Himarë, Albanian Riviera',
    region: 'Albanian Riviera',
    description: 'Hidden coves and dramatic cliffs along one of Europe\'s last undiscovered coastlines. Gjipe Beach and Livadhi await the discerning traveler.',
    image: 'https://commons.wikimedia.org/wiki/Special:FilePath/Himara_Potam_Beach_(2).jpg?width=1200',
    alt: 'Potam Beach in Himarë on the Albanian Riviera with dramatic cliffs, pebbled shore and clear Ionian water — hidden Mediterranean coastline',
    price: 'From €85',
    rating: 4.7,
    features: ['Secret Beaches', 'Cliff Diving', 'Old Town'],
    category: 'beach',
  },
  {
    id: 4,
    name: 'Durrës Seaside',
    location: 'Durrës, Central Coast',
    region: 'Central Albania',
    description: 'Albania\'s ancient port city where a 2,000-year-old amphitheater overlooks golden sandy beaches stretching along the Adriatic.',
    image: 'https://commons.wikimedia.org/wiki/Special:FilePath/Durr%C3%ABs_amphitheater_(Albania).JPG?width=1200',
    alt: 'Ancient Roman amphitheater of Durrës, Albania — 2,000-year-old ruins near the Adriatic coast and central Albania beaches',
    price: 'From €75',
    rating: 4.6,
    features: ['Roman Amphitheater', 'Sandy Beaches', 'Venetian Tower'],
    category: 'beach',
  },
  {
    id: 5,
    name: 'Berat — City of a Thousand Windows',
    location: 'Berat, Central Albania',
    region: 'Central Albania',
    description: 'A UNESCO World Heritage marvel where Ottoman-era white houses cascade down the hillside, their countless windows gazing across the Osum River valley.',
    image: 'https://commons.wikimedia.org/wiki/Special:FilePath/Berat,_Mangalem_quarter,_Albania.JPG?width=1200',
    alt: 'UNESCO World Heritage Berat, Albania — Mangalem quarter with Ottoman white stone houses and many windows cascading down the hillside above the Osum River',
    price: 'From €65',
    rating: 4.9,
    features: ['UNESCO Site', 'Berat Castle', 'Onufri Museum'],
    category: 'heritage',
  },
  {
    id: 6,
    name: 'Gjirokastër — City of Stone',
    location: 'Gjirokastër, Southern Albania',
    region: 'Southern Albania',
    description: 'A UNESCO-protected Ottoman city carved from stone, crowned by a magnificent fortress. Cobblestone streets whisper tales of centuries past.',
    image: 'https://commons.wikimedia.org/wiki/Special:FilePath/Gjirokast%C3%ABr_Castle_(by_Pudelek)_4_-_Clock_Tower.JPG?width=1200',
    alt: 'Gjirokastër Castle clock tower in southern Albania — UNESCO World Heritage Ottoman stone city fortress',
    price: 'From €60',
    rating: 4.9,
    features: ['UNESCO Site', 'Gjirokastër Castle', 'Bazaar'],
    category: 'heritage',
  },
  {
    id: 7,
    name: 'Korçë — Little Paris of Albania',
    location: 'Korçë, Southeastern Albania',
    region: 'Southern Albania',
    description: 'Albania\'s cultural capital, renowned for its elegant boulevards, vibrant arts scene, centuries-old breweries, and the stunning Voskopojë churches nearby.',
    image: 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/59/Korca_Old_Bazaar.jpg/1280px-Korca_Old_Bazaar.jpg',
    alt: 'Korçë Old Bazaar in southeastern Albania with pastel traditional buildings — cultural travel destination known as the Little Paris of Albania',
    price: 'From €55',
    rating: 4.7,
    features: ['Old Bazaar', 'Beer Festival', 'Medieval Art'],
    category: 'heritage',
  },
  {
    id: 8,
    name: 'Shkodër & Lake',
    location: 'Shkodër, Northern Albania',
    region: 'Northern Albania',
    description: 'The gateway to the Albanian Alps, where the largest lake in Southern Europe mirrors the surrounding mountains and Rozafa Castle stands sentinel.',
    image: 'https://commons.wikimedia.org/wiki/Special:FilePath/Lake_Shkod%C3%ABr_Behind_Rozafa_Castle.jpg?width=1200',
    alt: 'Lake Shkodër seen from Rozafa Castle in northern Albania — gateway to the Albanian Alps and largest lake in Southern Europe',
    price: 'From €70',
    rating: 4.8,
    features: ['Lake Shkodër', 'Rozafa Castle', 'Cycling Trails'],
    category: 'nature',
  },
  {
    id: 9,
    name: 'Theth National Park',
    location: 'Theth, Albanian Alps',
    region: 'Northern Albania',
    description: 'A pristine alpine paradise hidden in the Accursed Mountains. Stone tower houses, thundering waterfalls, and the legendary Blue Eye of Theth await.',
    image: 'https://commons.wikimedia.org/wiki/Special:FilePath/Theth_and_Theth_National_Park,_Albania_2017.jpg?width=1200',
    alt: 'Theth National Park village in the Albanian Alps with traditional stone kulla tower houses and Accursed Mountains peaks — Albania hiking destination',
    price: 'From €50',
    rating: 4.9,
    features: ['Hiking Trails', 'Grunas Waterfall', 'Lock-in Tower'],
    category: 'mountain',
  },
  {
    id: 10,
    name: 'Valbona Valley',
    location: 'Valbona, Albanian Alps',
    region: 'Northern Albania',
    description: 'The crown jewel of the Albanian Alps — a dramatic valley of towering peaks, emerald rivers, and traditional guesthouses offering legendary Albanian hospitality.',
    image: 'https://commons.wikimedia.org/wiki/Special:FilePath/Valbona_Valley_National_Park,_North_Albania.jpg?width=1200',
    alt: 'Valbona Valley National Park in the northern Albanian Alps with dramatic snow-capped Accursed Mountains peaks and emerald river — Theth to Valbona trek',
    price: 'From €45',
    rating: 4.8,
    features: ['Valbona Pass', 'River Rafting', 'Alpine Meadows'],
    category: 'mountain',
  },
  {
    id: 11,
    name: 'Blue Eye Spring',
    location: 'Muzinë, Southern Albania',
    region: 'Southern Albania',
    description: 'A mesmerizing natural phenomenon — a deep karst spring of impossibly blue water surrounded by ancient oak and sycamore forests. Nature\'s own jewel.',
    image: 'https://commons.wikimedia.org/wiki/Special:FilePath/Reservoir_overflow_at_Syri_i_kalter_(The_Blue_Eye),_Albania,_September_2022_01.jpg?width=1200',
    alt: 'Syri i Kaltër, the Blue Eye Spring near Muzinë in southern Albania — deep karst natural spring with vivid blue water surrounded by forest',
    price: 'From €40',
    rating: 4.8,
    features: ['Natural Spring', 'Forest Walks', 'Photography'],
    category: 'nature',
  },
  {
    id: 12,
    name: 'Butrint Archaeological Park',
    location: 'Butrint, Southern Albania',
    region: 'Albanian Riviera',
    description: 'A UNESCO World Heritage site spanning 2,500 years of civilization — from Greek and Roman ruins to Byzantine basilicas, set on a stunning lagoon peninsula.',
    image: 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/9b/Amphitheatre_of_Butrint_2009.jpg/1280px-Amphitheatre_of_Butrint_2009.jpg',
    alt: 'Ancient Greek and Roman amphitheater at Butrint Archaeological Park, southern Albania — UNESCO World Heritage site near Saranda and Ksamil',
    price: 'From €35',
    rating: 4.9,
    features: ['UNESCO Site', 'Roman Theater', 'Vivari Channel'],
    category: 'heritage',
  },
];

export const regions = ['All', 'Albanian Riviera', 'Central Albania', 'Southern Albania', 'Northern Albania'];
export const categories = ['all', 'beach', 'heritage', 'mountain', 'nature'] as const;
